import PropTypes from 'prop-types';
import React, { ComponentType } from 'react';
import Select, { createFilter, Props as IProps } from 'react-select';
import styled, { css } from 'styled-components';

import { Open, Remove, Search } from '@icons/tagsselect';

export const CLASS_NAME_PREFIX = 'selector';
const PLACEHOLDER_DEFAULT = 'Выберите...';
const NO_OPTIONS_DEFAULT = 'Ничего не найдено';

export const StyledSearchIcon = styled(Search)`
  cursor: pointer;
  flex-shrink: 0;
  height: 16px;
  width: 16px;
  opacity: 0;
`;

export const StyledSingleRemoveIcon = styled(Remove)`
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0;
  min-height: 12px;
  min-width: 12px;
  stroke: #046ad9;
`;

const StyledSelector = styled(Select)<{ isRequired?: boolean }>`
  .${CLASS_NAME_PREFIX}__menu {
    box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
    z-index: 2;
  }

  .${CLASS_NAME_PREFIX}__control {
    background: none;
    border: none;
    box-shadow: none;
    display: flex;
  }

  .${CLASS_NAME_PREFIX}__value-container {
    display: flex;
    box-sizing: content-box;
    flex: 1;
    padding: 0;
    position: relative;
  }

  .${CLASS_NAME_PREFIX}__multi-value {
    background-color: transparent;
    border: 1px solid #c2c5cc;
    border-radius: 13px;
    height: 26px;
    user-select: none;
  }

  .${CLASS_NAME_PREFIX}__multi-value__label {
    color: inherit;
    font-size: 14px;
    height: 24px;
    line-height: 24px;
    padding: 0 0 0 10px;
  }

  .${CLASS_NAME_PREFIX}__placeholder {
    color: #919499;
    cursor: pointer;
    font-size: 14px;
    margin: 0;
    position: initial;
    transform: initial;
    white-space: nowrap;
    position: absolute;
    width: 100%;
    left: 2px;

    ${({ isRequired }) =>
      isRequired &&
      css`
        &::after {
          content: ' *';
          color: red;
          font-size: 18px;
          font-weight: bold;
        }
      `}
  }

  .${CLASS_NAME_PREFIX}__menu-list {
    ::-webkit-scrollbar {
      width: 4px;
    }

    ::-webkit-scrollbar-thumb {
      background-color: #c2c5cc;
      border-radius: 40px;
    }
  }

  :hover {
    ${StyledSingleRemoveIcon},
    ${StyledSearchIcon} {
      opacity: 1;
    }
  }

  ${props =>
    props.isDisabled &&
    `
    cursor: default;
    user-select: none;
    opacity: 0.5;
  `}
`;

export const StyledValue = styled.div`
  align-items: center;
  display: flex;

  .${CLASS_NAME_PREFIX}__single-value {
    background-color: transparent;
    color: #17181a;
    font-size: 14px;
    height: 26px;
    line-height: 26px;
    margin: 2px 0;
    max-width: initial;
    position: initial;
    transform: initial;
    user-select: none;
  }
`;

export const StyledMultiValue = styled.div`
  align-items: center;
  display: flex;
  margin-right: 10px;
  overflow: hidden;
`;

export const StyledOpenIcon = styled(Open)`
  cursor: pointer;
  flex-shrink: 0;
  height: 6px;
  margin-left: 5px;
  width: 10px;
`;

export const StyledRemove = styled.div`
  cursor: pointer;
  padding: 0 8px !important;
  background: none !important;

  > svg {
    height: 10px;
    width: 10px;
  }

  :hover {
    background-color: transparent !important;
  }
`;

export const StyledRemoveIcon = styled(Remove)``;

export const StyledOption = styled.div<{
  isHighlighted: boolean;
  isSelected: boolean;
}>`
  ${({ isHighlighted }) =>
    isHighlighted &&
    css`
      font-size: 15px;
      font-weight: 600;
    `}

  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: #f3f9ff;
    `}

  cursor: pointer;
  line-height: 24px;
  padding: 10px;

  & + & {
    border-top: 1px solid #d8d8d8;
    line-height: 23px;
  }

  &:hover {
    background-color: #dcedff;
  }
`;

const getMultiValueRemove = ({ innerProps }) => (
  <StyledRemove {...innerProps}>
    <StyledRemoveIcon />
  </StyledRemove>
);

const getSingleValueRemove = ({ innerProps }) => (
  <StyledRemove {...innerProps}>
    <StyledSingleRemoveIcon />
  </StyledRemove>
);

const getDropdownIndicator = ({ innerProps }) => (
  <StyledSearchIcon {...innerProps} />
);

const getOption =
  customOption =>
  ({ children, innerProps, data, ...props }) =>
    (
      <StyledOption
        isHighlighted={data.isHighlighted}
        {...innerProps}
        isSelected={props.isSelected}
      >
        {customOption ? customOption({ data, ...props }) : children}
      </StyledOption>
    );

type State = {
  keyUpValue: string | null;
  isFocused: boolean;
};

class Selector extends React.PureComponent<
  IProps & {
    controlChildren: React.FC<any>;
    customFilter: Function;
    value: any;
  },
  State
> {
  static CLASS_NAME_PREFIX: string;

  static labelPropType: PropTypes.Requireable<string>;

  static StyledOpenIcon: ComponentType;

  static StyledValue: ComponentType;

  static optionPropTypes: PropTypes.Requireable<
    PropTypes.InferProps<{
      label: PropTypes.Validator<string>;
      value: PropTypes.Validator<string>;
      active?: PropTypes.Validator<boolean>;
    }>
  >;

  static valuePropType: PropTypes.Requireable<string>;

  static StyledRemove: ComponentType;

  static StyledMultiValue: ComponentType;

  static StyledSingleRemoveIcon: ComponentType;

  static StyledSearchIcon: ComponentType;

  static StyledRemoveIcon: ComponentType;

  static StyledOption: ComponentType;

  controlRef: React.RefObject<unknown>;

  constructor(props) {
    super(props);

    this.controlRef = React.createRef();

    const initialValue = props.value?.label || '';

    this.state = {
      keyUpValue: initialValue,
      isFocused: false,
    };

    this.setKeyUpValue = this.setKeyUpValue.bind(this);
    this.toggleFocus = this.toggleFocus.bind(this);
  }

  toggleFocus = ({ target }) => {
    this.setState({
      isFocused: target === document.activeElement,
    });
  };
  setKeyUpValue(ev) {
    this.setState({ keyUpValue: ev?.target?.value });
  }

  render() {
    const {
      children,
      components = {},
      controlChildren,
      isMulti,
      isClearable,
      isSearchable,
      isLoading,
      placeholder = PLACEHOLDER_DEFAULT,
      closeMenuOnSelect,
      hideSelectedOptions,
      noOptionsMessage,
      blurInputOnSelect = true,
      customFilter,
      ...props
    } = this.props;

    const { keyUpValue, isFocused } = this.state;

    const {
      ClearIndicator,
      MultiValueRemove,
      DropdownIndicator,
      Option,
      ...otherComponents
    }: any = components;

    const filteredComponents = {
      MultiValueRemove: MultiValueRemove || getMultiValueRemove,
      ClearIndicator: ClearIndicator || getSingleValueRemove,
      Option: Option || getOption(children),
      DropdownIndicator: DropdownIndicator || getDropdownIndicator,
      IndicatorSeparator: null,
      ...otherComponents,
    };

    const shouldListenKeyUp = !isMulti && !isClearable;

    return (
      <div {...(shouldListenKeyUp && { onKeyUp: this.setKeyUpValue })}>
        <StyledSelector
          {...props}
          isLoading={isLoading}
          classNamePrefix={CLASS_NAME_PREFIX}
          components={filteredComponents}
          isClearable={isClearable}
          isMulti={isMulti}
          isSearchable={isSearchable}
          placeholder={placeholder}
          closeMenuOnSelect={closeMenuOnSelect}
          escapeClearsValue
          backspaceRemovesValue
          hideSelectedOptions={hideSelectedOptions}
          blurInputOnSelect={blurInputOnSelect}
          {...(shouldListenKeyUp && {
            onFocus: this.toggleFocus,
            onBlur: this.toggleFocus,
            ...(isFocused && keyUpValue === '' && { value: null }),
          })}
          noOptionsMessage={noOptionsMessage || (() => NO_OPTIONS_DEFAULT)}
          {...(customFilter && {
            filterOption: option => {
              const isInternalFiltered = Boolean(
                createFilter()(option, String(keyUpValue))
              );
              const isCustomFiltered = customFilter(option);
              return isInternalFiltered && isCustomFiltered;
            },
            onFocus: ev => {
              const { value } = props;
              if (!value || value?.active === false) {
                this.setKeyUpValue(ev);
              }
            },
          })}
        />
      </div>
    );
  }
}

Selector.CLASS_NAME_PREFIX = CLASS_NAME_PREFIX;
Selector.StyledOpenIcon = StyledOpenIcon;
Selector.StyledValue = StyledValue;
Selector.StyledOption = StyledOption;
Selector.StyledRemoveIcon = StyledRemoveIcon;
Selector.StyledSearchIcon = StyledSearchIcon;
Selector.StyledSingleRemoveIcon = StyledSingleRemoveIcon;
Selector.StyledMultiValue = StyledMultiValue;
Selector.StyledRemove = StyledRemove;

export default Selector;
